import React, { useCallback, useRef, useState } from "react";
import {
  X,
  User,
  Phone,
  Users,
  Save,
  Loader2,
  Mail,
  UserRoundPlus,
} from "lucide-react";
import { IRegisteredUser } from "../../models/epcCustomer.model";
import { getUser } from "../../utils/auth";
import Button, { ButtonType } from "../Assets/Button";
import Select from "react-select";
import { useInfiniteSalesReps } from "../../hooks/useInfiniteSalesReps";

interface AddCustomerModalProps {
  onClose: () => void;
  onSubmit: (customerData: IRegisteredUser) => void | Promise<void>;
  isLoading?: boolean;
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const currentUser = getUser();

  const {
    salesReps: infiniteSalesReps,
    isLoadingMore,
    hasMore,
    loadMore,
    handleSearch,
  } = useInfiniteSalesReps(true);

  const [formData, setFormData] = useState<IRegisteredUser>({
    customerName: "",
    mobileNumber: "",
    email: "",
    salesRepresentative: undefined,
    cseID: currentUser?.id || undefined,
  });

  const [errors, setErrors] = useState<Partial<IRegisteredUser>>({});

  const selectRef = useRef<any>(null);

  const handleInputChange = (field: keyof IRegisteredUser, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<IRegisteredUser> = {};

    if (!formData.customerName)
      newErrors.customerName = "Customer name is required";

    if (!formData.mobileNumber)
      newErrors.mobileNumber = "Mobile number is required";

    if (!formData.salesRepresentative && !formData.cseID)
      newErrors.salesRepresentative = "Either Sales Rep or CSE is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const salesRepOptions = [
    {
      value: currentUser?.id || "",
      label: `Self - ${currentUser?.username}`,
    },
    ...infiniteSalesReps.map((rep: any) => ({
      value: rep._id,
      label: `${rep.name} (${rep.mobileNumber})`,
    })),
  ];

  const MenuList = (props: any) => {
    const { children } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        const bottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

        if (bottom && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      [hasMore, isLoadingMore, loadMore],
    );

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMore && (
          <div className="flex justify-center items-center p-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  const selectedSalesRep = salesRepOptions.find(
    (opt) =>
      opt.value ===
      (formData.salesRepresentative
        ? typeof formData.salesRepresentative === "string"
          ? formData.salesRepresentative
          : formData.salesRepresentative._id
        : typeof formData.cseID === "string"
          ? formData.cseID
          : formData.cseID?._id),
  );

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        customerName: "",
        mobileNumber: "",
        email: "",
        salesRepresentative: undefined,
        cseID: currentUser?.id || undefined,
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-xl bg-white shadow-xl border border-gray-100">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-50 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <div className="w-8 h-8 flex items-center justify-center">
                  <UserRoundPlus className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add Customer
                  </h3>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="p-1.5 text-gray-400 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form className="p-5 space-y-4">
            {/* Row 1: Customer Name and Mobile Number */}
            <div className="grid grid-cols-2 gap-3">
              {/* Customer Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gray-500" />
                  Customer Name
                </label>
                <input
                  type="text"
                  value={formData?.customerName}
                  onChange={(e) =>
                    handleInputChange("customerName", e.target.value)
                  }
                  placeholder="Enter full name"
                  disabled={isLoading}
                  className={`w-full px-3 py-2.5 bg-white border rounded-lg focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 disabled:opacity-50 text-sm ${
                    errors.customerName
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                />
                {errors.customerName && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {errors.customerName}
                  </p>
                )}
              </div>

              {/* Mobile Number */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-gray-500" />
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) =>
                    handleInputChange("mobileNumber", e.target.value)
                  }
                  placeholder="9876543210"
                  disabled={isLoading}
                  className={`w-full px-3 py-2.5 bg-white border rounded-lg focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono disabled:opacity-50 text-sm ${
                    errors.mobileNumber
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                />
                {errors.mobileNumber && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {errors.mobileNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: Customer Email and Sales Representative */}
            <div className="grid grid-cols-2 gap-3">
              {/* Customer Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-500" />
                  Customer Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email address"
                  disabled={isLoading}
                  className={`w-full px-3 py-2.5 bg-white border rounded-lg focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 disabled:opacity-50 text-sm ${
                    errors.email
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>
                )}
              </div>

              {/* Sales Representative */}
              <div className="space-y-1.5 text-xs">
                <label className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-gray-500" />
                  Sales Representative
                </label>
                <Select
                  ref={selectRef}
                  options={salesRepOptions}
                  value={selectedSalesRep || null}
                  onChange={(selectedOption: { value: string } | null) => {
                    const selectedValue = selectedOption?.value;

                    if (selectedValue === currentUser?.id) {
                      setFormData((prev) => ({
                        ...prev,
                        cseID: currentUser?.id || "",
                        salesRepresentative: undefined,
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        salesRepresentative: selectedValue,
                        cseID: undefined,
                      }));
                    }
                  }}
                  onInputChange={(inputValue, actionMeta) => {
                    if (actionMeta.action === "input-change") {
                      handleSearch(inputValue);
                    }
                  }}
                  isClearable
                  isDisabled={isLoading}
                  placeholder="Select Sales Rep..."
                  components={{ MenuList }}
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      minHeight: "40px",
                      borderColor: errors.salesRepresentative
                        ? "#fca5a5"
                        : "#e5e7eb",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: errors.salesRepresentative
                          ? "#f87171"
                          : "#d1d5db",
                      },
                    }),
                    menu: (base: any) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
                {errors.salesRepresentative && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {typeof errors.salesRepresentative === "string"
                      ? errors.salesRepresentative
                      : ""}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-3">
              <Button
                type={ButtonType.Cancel}
                onClick={handleClose}
                disabled={isLoading}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.Submit}
                onClick={handleSubmit}
                disabled={isLoading}
                className="text-xs inline-flex items-center gap-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    Create
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;
